import { element } from "./dom.js";
import {
  footerHeight,
  pageMargin,
  type PaperSize,
  paperSizes,
  titleHeight,
} from "./worksheet-layout.js";
import type { WorksheetPaper } from "./worksheet-state.js";

/**
 * The page every kind of sheet is drawn on: a box the size of the paper, its
 * margin, a heading over each part and a footer linking back to the sheet.
 *
 * The character and pinyin sheets lay out what goes inside differently and
 * print it the same way, so this is the one place the page itself is drawn.
 */

/** What the sheet says at its head and foot. */
export interface SheetLabels {
  readonly title: string;

  /** What the footer names the site as. */
  readonly site: string;

  /**
   * The sheet's own address, settings included.
   *
   * The footer links to it, so a PDF passed from one teacher to another
   * opens the generator on the same sheet.
   */
  readonly address: string;

  /** The site the 词典 entries are linked on, such as `https://www.chineseboost.com`. */
  readonly origin: string;
}

/** One part of a sheet: its heading and what goes on each of its pages. */
export interface SheetPart {
  readonly title: string;

  /** A line under the heading saying what to do, if the part needs one. */
  readonly instructions?: string;

  readonly pages: readonly (readonly Node[])[];
}

function mm(value: number): string {
  return `${String(Math.round(value * 100) / 100)}mm`;
}

/**
 * Give an element lengths in millimetres, through the CSSOM.
 *
 * The site's Content-Security-Policy allows no inline styles, and that covers
 * a `style` attribute written into markup or set with `setAttribute`. A
 * property set on `element.style` is a script's own change and passes.
 */
export function sized<Sized extends HTMLElement>(
  element: Sized,
  lengths: Readonly<Record<string, number>>,
): Sized {
  for (const [property, length] of Object.entries(lengths)) {
    element.style.setProperty(property, mm(length));
  }

  return element;
}

/** The room a sheet has inside its margins, less the footer. */
export function pageRoom(paper: WorksheetPaper): {
  readonly paper: PaperSize;
  readonly width: number;
  readonly height: number;
} {
  const size = paperSizes[paper];

  return {
    paper: size,
    width: size.width - 2 * pageMargin,
    height: size.height - 2 * pageMargin - footerHeight,
  };
}

/** The instructions under a part's heading, room for two lines. */
export const instructionsHeight = 10;

/** How much room a part's heading takes on its first page. */
export function partHeadHeight(part: {
  readonly instructions?: string;
}): number {
  return (
    titleHeight + (part.instructions === undefined ? 0 : instructionsHeight)
  );
}

function partHead(part: SheetPart): HTMLElement {
  const children: Node[] = [element("h2", { text: part.title })];
  if (part.instructions !== undefined) {
    children.push(
      element("p", {
        className: "cb-ws-instructions",
        text: part.instructions,
      }),
    );
  }

  return sized(element("header", { className: "cb-ws-title", children }), {
    height: partHeadHeight(part),
  });
}

/**
 * Every page of a sheet, each part's heading over its first page and every
 * page numbered out of the whole.
 */
export function drawPages(
  paper: WorksheetPaper,
  parts: readonly SheetPart[],
  labels: SheetLabels,
): readonly HTMLElement[] {
  const size = paperSizes[paper];
  const total = parts.reduce((sum, part) => sum + part.pages.length, 0);
  const drawn: HTMLElement[] = [];

  for (const part of parts) {
    for (const [at, children] of part.pages.entries()) {
      const number = drawn.length + 1;
      const page = element("section", {
        className: "cb-ws-page",
        attributes: {
          "data-paper": paper,
          "aria-label": `Page ${String(number)} of ${String(total)}`,
        },
        children: [
          element("div", {
            className: "cb-ws-body",
            children: at === 0 ? [partHead(part), ...children] : children,
          }),
          element("footer", {
            className: "cb-ws-footer",
            children: [
              element("a", {
                text: labels.site,
                attributes: { href: labels.address },
              }),
              element("span", {
                text: `Page ${String(number)} of ${String(total)}`,
              }),
            ],
          }),
        ],
      });
      drawn.push(
        sized(page, {
          width: size.width,
          height: size.height,
          padding: pageMargin,
        }),
      );
    }
  }

  return drawn;
}
